import { FORM_OPERATION, Question } from "@archetypes/form";
import { formOperation } from "@utils/form-delta.util";
import { startAppListening } from "../listener-middleware";
import {
  addPage,
  addQuestion,
  clonePage,
  cloneQuestion,
  cloneFieldRules,
  copyQuestion,
  movePage,
  moveQuestion,
  removePage,
  removeQuestion,
  updateFieldRules,
  updatePage,
  updateQuestion,
} from "../slices/form.slice";

startAppListening({
  actionCreator: addPage,
  effect: (action, api) => {
    const { appId, formId, pageId, id, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newPageIndex = formState.formData.pages.findIndex((p) => p.id === id);
    if (newPageIndex === -1) return;
    const newPage = formState.formData.pages[newPageIndex];
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.PAGE_BREAK,
      metadata: { position: newPageIndex, page: newPage },
    });
  },
});

startAppListening({
  actionCreator: clonePage,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newPageId = formState.lastClonedQuestionOrPageId;
    const position = formState.formData.pages.findIndex((p) => p.id === newPageId);
    if (position === -1) return;
    const clonedPage = formState.formData.pages[position];
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.CLONE_PAGE,
      metadata: { position, page: clonedPage },
    });
  },
});

startAppListening({
  actionCreator: movePage,
  effect: (action, api) => {
    const { appId, formId, moveablePageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const position = formState.formData.pages.findIndex((p) => p.id === moveablePageId);
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.MOVE_PAGE,
      metadata: { position, page: { id: moveablePageId } },
    });
  },
});

startAppListening({
  actionCreator: addQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, id, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const questions = formState.formData.pages[pageIndex].questions;
    const position = questions.findIndex((q) => q.id === id);
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.ADD_QUESTION,
      metadata: { position, question: questions[position] },
    });
  },
});

startAppListening({
  actionCreator: updatePage,
  effect: (action, api) => {
    const { appId, formId, page, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const position = formState.formData.pages.findIndex((p) => p.id === page.id);
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.UPDATE_PAGE,
      metadata: { position, page: { ...page, questions: undefined } },
    });
  },
});

startAppListening({
  actionCreator: updateQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, questionId, question, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = formState.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === questionId,
    );
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.UPDATE_QUESTION,
      metadata: { position, question },
    });
  },
});

startAppListening({
  actionCreator: cloneQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newQuestionId = formState.lastClonedQuestionOrPageId;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = formState.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === newQuestionId,
    );
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.CLONE_QUESTION,
      metadata: {
        question: formState.formData.pages[pageIndex].questions[position],
        position,
      },
    });
  },
});

startAppListening({
  actionCreator: moveQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, client, currentQuestionId, targetQuestionPageId } =
      action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const targetPageIndex = formState.formData.pages.findIndex(
      (p) => p.id === targetQuestionPageId,
    );
    if (targetPageIndex === -1) return;
    const position = formState.formData.pages[targetPageIndex].questions.findIndex(
      (q) => q.id === currentQuestionId,
    );
    if (position === -1) return;
    const question: Question = JSON.parse(
      JSON.stringify(formState.formData.pages[targetPageIndex].questions[position]),
    );
    formOperation({
      appId,
      formId,
      pageId,
      targetPageId: targetQuestionPageId,
      client,
      type: FORM_OPERATION.MOVE_QUESTION,
      metadata: { question, position },
    });
  },
});

startAppListening({
  actionCreator: copyQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newQuestionId = formState.lastClonedQuestionOrPageId;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const questions = formState.formData.pages[pageIndex].questions;
    const position = questions.findIndex((q) => q.id === newQuestionId);
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.ADD_QUESTION,
      metadata: { position, question: questions[position] },
    });
  },
});

startAppListening({
  actionCreator: removePage,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const previous = api.getOriginalState().form[formId];
    if (!previous) return;
    const position = previous.formData.pages.findIndex((p) => p.id === pageId);
    if (position === -1) return;
    const page = JSON.parse(JSON.stringify(previous.formData.pages[position]));
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.REMOVE_PAGE,
      metadata: { position, page: { ...page, questions: undefined } },
    });
  },
});

startAppListening({
  actionCreator: removeQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, questionId, client } = action.payload;
    const previous = api.getOriginalState().form[formId];
    if (!previous) return;
    const pageIndex = previous.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = previous.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === questionId,
    );
    if (position === -1) return;
    const question: Question = JSON.parse(
      JSON.stringify(previous.formData.pages[pageIndex].questions[position]),
    );
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.REMOVE_QUESTION,
      metadata: { question, position },
    });
  },
});

startAppListening({
  actionCreator: updateFieldRules,
  effect: (action) => {
    const { type, rule, appId, formId, client } = action.payload;
    const operationType =
      type === "DELETE"
        ? FORM_OPERATION.DELETE_QUESTION_CONDITION
        : type === "UPDATE"
        ? FORM_OPERATION.UPDATE_QUESTION_CONDITION
        : FORM_OPERATION.ADD_QUESTION_CONDITION;
    formOperation({
      appId,
      formId,
      pageId: rule.pageId,
      questionId: rule.questionId,
      ruleId: rule.id,
      client,
      type: operationType,
      metadata: { fieldRule: rule },
    });
  },
});

startAppListening({
  actionCreator: cloneFieldRules,
  effect: (action, api) => {
    const { appId, formId, client } = action.payload;
    const before = api.getOriginalState().form[formId]?.formData.fieldRules ?? [];
    const after = api.getState().form[formId]?.formData.fieldRules ?? [];
    const addedRules = after.slice(before.length);
    if (addedRules.length === 0) return;
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.BULK_QUESTION_CONDITION,
      metadata: { bulkFieldRules: addedRules },
    });
  },
});
